
import { memo, useEffect, useRef, useState } from "react";
import AudioPlayer from "@/components/ui/AudioPlayer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/hooks/useAuth";

interface Comment {
  content: string;
  user_name: string;
  play_url?: string;
}

const CommentText = ({ content }: { content: string }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [open, setOpen] = useState(false);

  const cancelClose = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkOverflow = () => {
      setOverflowing(element.scrollWidth > element.clientWidth + 1);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [content]);

  const closeSoon = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <Popover
      open={overflowing && open}
      onOpenChange={(next) => {
        if (next) return;
        setOpen(false);
      }}
    >
      <PopoverTrigger asChild>
        <div
          ref={textRef}
          className="mb-2 truncate text-xs text-gray-700"
          onMouseEnter={() => {
            cancelClose();
            if (overflowing) setOpen(true);
          }}
          onMouseLeave={closeSoon}
          onClick={(event) => {
            event.preventDefault();
            setOpen(false);
          }}
        >
          {content}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-3 text-xs leading-5 text-gray-700 break-words"
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={closeSoon}
        onClick={(event) => event.stopPropagation()}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
};

interface CommentsDisplayProps {
  comments?: Comment[] | any[];
  hasComments?: boolean;
  loading?: boolean;
  expanded?: boolean;
}

const CommentsDisplay = memo(({ comments, hasComments, loading, expanded = false }: CommentsDisplayProps) => {
  const { user } = useAuth();
  // Show audio player only if user has feature_access_id = 1 AND 2 (both required)
  const shouldShowAudio = (user?.feature_access_ids?.includes(1) && user?.feature_access_ids?.includes(2)) ?? false;
  
  if (loading) {
    return (
      <div className="bg-gray-50 p-3 rounded">
        <div className="text-xs text-gray-400 italic">Loading comments...</div>
      </div>
    );
  }
  
  if (comments && comments.length > 0) {
    return (
      <div className="min-w-0 bg-gray-50 p-3 rounded">
        <div className="space-y-2">
          {comments.slice(0, expanded ? 3 : 1).map((comment, index) => (
            <div key={index} className="relative min-w-0 pl-4">
              {/* Light blue vertical line */}
              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-200"></div>
              {/* User name in blue */}
              <div className="font-medium text-blue-600 text-sm mb-1">
                {comment.user_name || 'Unknown User'}
              </div>
              {/* Comment text */}
              {comment.content && <CommentText content={comment.content} />}
              {/* Audio player for call recording - only show if user has feature_access_id = 1 */}
              {comment.play_url && shouldShowAudio && (
                <div className="mt-2 min-w-0 max-w-full">
                  <AudioPlayer audioUrl={comment.play_url} />
                </div>
              )}
            </div>
          ))}
          {expanded && comments.length > 3 && (
            <div className="text-xs text-blue-600 italic mt-2">
              +{comments.length - 3} more comments
            </div>
          )}
        </div>
      </div>
    );
  }

  // For empty state, show a simple indicator
  return (
    <div className="text-xs text-gray-400 italic">
      {hasComments ? 'Has comments - click to view' : 'Click to add comments'}
    </div>
  );
});

CommentsDisplay.displayName = "CommentsDisplay";

export default CommentsDisplay;
